import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';
import FeedbackTrend from './TrendChart';

// Validated categorical slots (blue, aqua, orange, violet: all adjacent
// pairs pass CVD and normal-vision separation on white). Waiting is a
// neutral status, not a series. Aqua sits under 3:1, so there is a legend
// and a table view.
const OUTCOMES = [
  { key: 'verbatim', label: 'Sent as written', color: '#2a78d6' },
  { key: 'adapted', label: 'Sent after edits', color: '#1baf7a' },
  { key: 'ignored', label: 'Replaced by the team', color: '#eb6834' },
  { key: 'handoff', label: 'Note for an agent', color: '#4a3aa7' },
  { key: 'waiting', label: 'Waiting for the team', color: '#c9ced3' },
];

const pct = (a, b) => (b ? Math.round((a / b) * 100) : null);
const share = (a, b) => (b ? `${pct(a, b)}%` : '–');
const day = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const mins = (m) => (m == null ? null : m < 90 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);
const label = (c) => ((c || '').startsWith('noise') ? 'Not a customer' : (c || 'unknown').replace(/_/g, ' '));

const BLUE = '#2a78d6';
const TRACK = '#ebeef0';

const Tile = ({ children, className = '' }) => (
  <div className={`rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 ${className}`}>{children}</div>
);

// Percentage ring: one hue on a neutral track, value in the middle.
const Ring = ({ value, size = 88, stroke = 9, color = BLUE, label: aria }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={aria}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
      {value != null && (
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${(c * v) / 100} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      )}
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize={size * 0.24} fontWeight="600" fill="currentColor">
        {value == null ? '-' : `${value}%`}
      </text>
    </svg>
  );
};

const RingKpi = ({ value, label: l, detail, color }) => (
  <Tile className="flex items-center gap-4">
    <Ring value={value} color={color} label={`${l}: ${value ?? 'no data'}%`} />
    <div className="min-w-0">
      <div className="font-medium">{l}</div>
      {detail && <div className="mt-0.5 text-sm text-[var(--ink-3)]">{detail}</div>}
    </div>
  </Tile>
);

// Daily trend for a count: thin line, last point marked, hover shows the day.
const Spark = ({ values, dates, color = BLUE }) => {
  const [h, setH] = useState(null);
  if (!values.length) return null;
  const W = 160, H = 40, max = Math.max(1, ...values);
  const x = (i) => (values.length === 1 ? W / 2 : (i / (values.length - 1)) * (W - 8) + 4);
  const y = (v) => H - 4 - (v / max) * (H - 10);
  const pts = values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const last = values.length - 1;
  return (
    <div className="relative mt-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-10 w-full" preserveAspectRatio="none" onMouseLeave={() => setH(null)}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {values.map((v, i) => (
          <rect key={i} x={x(i) - W / values.length / 2} y="0" width={W / values.length} height={H} fill="transparent" onMouseEnter={() => setH(i)} />
        ))}
        <circle cx={x(h ?? last)} cy={y(values[h ?? last])} r="3.5" fill={color} stroke="var(--surface)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      {h != null && (
        <div className="pointer-events-none absolute -top-7 rounded border border-[var(--line)] bg-[var(--surface)] px-2 py-0.5 text-xs shadow-sm"
          style={{ left: `${Math.min(70, (x(h) / W) * 100)}%` }}>
          {day(dates[h])}: <span className="num font-semibold">{values[h]}</span>
        </div>
      )}
    </div>
  );
};

const CountKpi = ({ value, label: l, detail, values, dates }) => (
  <Tile>
    <div className="flex items-baseline justify-between gap-3">
      <div className="num text-[2rem] font-semibold leading-none">{value ?? '-'}</div>
      <div className="text-right font-medium">{l}</div>
    </div>
    {detail && <div className="mt-1 text-right text-sm text-[var(--ink-3)]">{detail}</div>}
    {values && values.length > 1 && <Spark values={values} dates={dates} />}
  </Tile>
);

// Customer -> note -> team reply, proportional segments.
const TimelineKpi = ({ toNote, toReply }) => {
  const a = toNote || 0, b = toReply || 0, total = a + b || 1;
  return (
    <Tile>
      <div className="font-medium">Response time (median)</div>
      <div className="mt-4 flex h-3 w-full gap-[2px] overflow-hidden rounded-full bg-[var(--line-2)]">
        {toNote != null && <div className="h-full rounded-l-full" style={{ width: `${Math.max(4, (a / total) * 100)}%`, background: BLUE }} title={`Customer to note: ${mins(toNote)}`} />}
        {toReply != null && <div className="h-full rounded-r-full" style={{ width: `${(b / total) * 100}%`, background: '#9fc3ee' }} title={`Note to team reply: ${mins(toReply)}`} />}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div><span className="inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: BLUE }} /> <span className="text-[var(--ink-2)]">Customer to note</span>
          <div className="num text-[1.4rem] font-semibold">{mins(toNote) ?? '-'}</div></div>
        <div className="text-right"><span className="inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: '#9fc3ee' }} /> <span className="text-[var(--ink-2)]">Note to team reply</span>
          <div className="num text-[1.4rem] font-semibold">{mins(toReply) ?? '-'}</div></div>
      </div>
    </Tile>
  );
};

// Funnel: each step's bar is proportional to the first step; the gap
// between steps says how many left the path and why.
const FunnelVertical = ({ steps }) => {
  const first = steps[0].value || 1;
  return (
    <ol className="space-y-1">
      {steps.map((st, i) => {
        const prev = i ? steps[i - 1].value : null;
        const w = Math.max(2, (st.value / first) * 100);
        return (
          <li key={st.label}>
            {i > 0 && st.drop && (
              <div className="flex items-center justify-center gap-2 py-1 text-sm text-[var(--ink-3)] sm:pl-52 sm:pr-28">
                <span aria-hidden>↳</span>
                {st.drop.map((dd, j) => <span key={dd.label}>{j ? ' · ' : ''}<span className="num font-medium text-[var(--ink-2)]">{dd.value}</span> {dd.label}</span>)}
              </div>
            )}
            <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-stretch max-sm:gap-1">
              <div className="w-52 shrink-0 text-[0.95rem] font-medium max-sm:w-auto">{st.label}</div>
              <div className="flex h-10 flex-1 justify-center">
                <div className="h-full rounded-md transition-all" style={{ width: `${w}%`, background: st.color || BLUE, opacity: 1 - i * 0.09 }} title={`${st.label}: ${st.value}`} />
              </div>
              <div className="w-28 shrink-0 text-right max-sm:w-auto max-sm:text-left">
                <span className="num text-[1.25rem] font-semibold">{st.value}</span>
                {prev ? <span className="num ml-1.5 text-sm text-[var(--ink-3)]">{pct(st.value, prev)}%</span> : null}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// Horizontal funnel: stages left to right, bar height proportional to the
// first stage, tapered connectors between them. Below each stage: the count,
// its share of the previous stage and what dropped out on the way in.
const FunnelHorizontal = ({ steps }) => {
  const first = steps[0].value || 1;
  const H = 170;
  const h = (v) => Math.max(6, (v / first) * H);
  const cols = steps.map((_, i) => (i ? '44px minmax(0,1fr)' : 'minmax(0,1fr)')).join(' ');
  return (
    <div>
      <div className="grid items-center" style={{ gridTemplateColumns: cols, height: H }}>
        {steps.map((st, i) => (
          <React.Fragment key={st.label}>
            {i > 0 && (
              <svg viewBox={`0 0 44 ${H}`} preserveAspectRatio="none" className="h-full w-full" aria-hidden>
                <polygon fill={BLUE} opacity={0.18}
                  points={`0,${(H - h(steps[i - 1].value)) / 2} 44,${(H - h(st.value)) / 2} 44,${(H + h(st.value)) / 2} 0,${(H + h(steps[i - 1].value)) / 2}`} />
              </svg>
            )}
            <div className="flex h-full items-center">
              <div className="w-full rounded-md" style={{ height: h(st.value), background: BLUE, opacity: 1 - i * 0.09 }} title={`${st.label}: ${st.value}`} />
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 grid" style={{ gridTemplateColumns: cols }}>
        {steps.map((st, i) => {
          const prev = i ? steps[i - 1].value : null;
          return (
            <React.Fragment key={st.label}>
              {i > 0 && <div />}
              <div className="min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className="num text-[1.5rem] font-semibold leading-none">{st.value}</span>
                  {prev ? <span className="num text-sm text-[var(--ink-3)]">{pct(st.value, prev)}%</span> : null}
                </div>
                <div className="mt-1 text-[0.95rem] font-medium leading-snug">{st.label}</div>
                {st.drop && st.drop.map((dd) => (
                  <div key={dd.label} className="mt-1 text-[0.82rem] leading-snug text-[var(--ink-3)]">
                    <span className="num font-medium text-[var(--ink-2)]">−{dd.value}</span> {dd.label}
                  </div>
                ))}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const Funnel = ({ steps }) => (
  <>
    <div className="hidden md:block"><FunnelHorizontal steps={steps} /></div>
    <div className="md:hidden"><FunnelVertical steps={steps} /></div>
  </>
);

// One stacked bar per topic: what the team did with its notes.
const OutcomeBar = ({ row }) => {
  const total = OUTCOMES.reduce((t, o) => t + (row[o.key] || 0), 0) || 1;
  return (
    <div className="flex h-3 w-full gap-[2px] overflow-hidden rounded-full bg-[var(--line-2)]">
      {OUTCOMES.filter((o) => row[o.key]).map((o) => (
        <div key={o.key} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${(row[o.key] / total) * 100}%`, background: o.color }}
          title={`${o.label}: ${row[o.key]}`} />
      ))}
    </div>
  );
};

const DailyChart = ({ rows }) => {
  const [hover, setHover] = useState(null);
  const W = 1200, H = 280, padL = 40, padB = 28, padT = 10;
  const totals = rows.map((r) => OUTCOMES.reduce((t, o) => t + r[o.key], 0));
  const max = Math.max(5, ...totals);
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const band = (W - padL) / Math.max(rows.length, 1);
  const barW = Math.min(24, band * 0.5);
  const y = (v) => padT + (H - padT - padB) * (1 - v / top);
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Bible agent notes per day by what the team did with them">
        {[0, top / 2, top].map((t) => (
          <g key={t}>
            <line x1={padL} x2={W} y1={y(t)} y2={y(t)} stroke="#ebeef0" />
            <text x={padL - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#8a939e">{t}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = padL + i * band + (band - barW) / 2;
          const segs = OUTCOMES.filter((o) => r[o.key] > 0);
          let acc = 0;
          return (
            <g key={r.date} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={padL + i * band} y={padT} width={band} height={H - padT - padB} fill="transparent" />
              {segs.map((o, j) => {
                const y0 = y(acc), y1 = y(acc + r[o.key]);
                acc += r[o.key];
                const h = Math.max(0, y0 - y1 - (j < segs.length - 1 ? 2 : 0));
                return j === segs.length - 1 ? (
                  <path key={o.key} fill={o.color} d={`M${x},${y0} V${y1 + 4} Q${x},${y1} ${x + 4},${y1} H${x + barW - 4} Q${x + barW},${y1} ${x + barW},${y1 + 4} V${y0} Z`} />
                ) : <rect key={o.key} x={x} y={y0 - h} width={barW} height={h} fill={o.color} />;
              })}
              {totals[i] > 0 && <text x={x + barW / 2} y={y(totals[i]) - 6} textAnchor="middle" fontSize="11" fill="#5a6472">{totals[i]}</text>}
              <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="#8a939e">{day(r.date)}</text>
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <div className="pointer-events-none absolute top-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-sm shadow-sm"
          style={{ left: `${Math.min(78, ((hover + 0.5) / rows.length) * 100)}%` }}>
          <div className="font-semibold">{day(rows[hover].date)}</div>
          {OUTCOMES.map((o) => (
            <div key={o.key} className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: o.color }} />
              <span className="text-[var(--ink-2)]">{o.label}</span>
              <span className="num ml-auto pl-4">{rows[hover][o.key]}</span>
            </div>
          ))}
          {rows[hover].held > 0 && <div className="text-[var(--ink-3)]">No note posted: {rows[hover].held}</div>}
        </div>
      )}
    </div>
  );
};

const Table = ({ head, children }) => (
  <table className="w-full text-left text-[0.95rem]">
    <thead><tr className="border-b border-[var(--line)] text-sm text-[var(--ink-3)]">{head.map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}</tr></thead>
    <tbody className="num">{children}</tbody>
  </table>
);

// Scheduled replies: customer tickets Replai answered by itself (Geoff, 2026-10-20).
const CAT_LABEL = { order_status: 'Order status', product_question: 'Product questions', pre_sales_inquiry: 'Pre-sales', tracking: 'Tracking' };

const ScheduledStats = ({ s }) => {
  const max = Math.max(1, ...s.daily.map((x) => x.tickets));
  const how = s.sent_how || {};
  return (
    <section className="mt-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
      <h2 className="text-[1.2rem] font-semibold">Answered by Replai</h2>
      <p className="mt-1 text-[var(--ink-2)]">Customer tickets where Replai sent the reply itself, through the scheduled replies.</p>
      <div className="mt-5 grid gap-6 sm:grid-cols-3">
        <div>
          <div className="num text-[2.4rem] font-semibold leading-none">{s.share_sent == null ? '–' : `${s.share_sent}%`}</div>
          <div className="mt-1 text-sm text-[var(--ink-2)]"><b className="num">{s.sent}</b> of <b className="num">{s.tickets}</b> customer tickets</div>
        </div>
        <div className="text-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">How they went out</div>
          <ul className="mt-1 space-y-0.5 text-[var(--ink-2)]">
            <li><b className="num text-[var(--ink)]">{how.untouched || 0}</b> nobody looked, sent as drafted</li>
            <li><b className="num text-[var(--ink)]">{how.approved || 0}</b> approved by the team as they were</li>
            <li><b className="num text-[var(--ink)]">{how.edited || 0}</b> edited by the team first</li>
          </ul>
        </div>
        <div className="text-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">Queued but not sent</div>
          <ul className="mt-1 space-y-0.5 text-[var(--ink-2)]">
            {(s.status?.blocked || 0) > 0 && <li><b className="num text-[var(--ink)]">{s.status.blocked}</b> blocked by the team</li>}
            {(s.held_why || []).map(([why, n]) => <li key={why}><b className="num text-[var(--ink)]">{n}</b> {why}</li>)}
            {((s.status?.queued || 0) + (s.status?.approved || 0)) > 0 && <li><b className="num text-[var(--ink)]">{(s.status.queued || 0) + (s.status.approved || 0)}</b> still to go out</li>}
            {(s.status?.failed || 0) > 0 && <li className="text-red-700"><b className="num">{s.status.failed}</b> failed</li>}
            {!s.status?.blocked && !(s.held_why || []).length && !s.status?.queued && !s.status?.approved && !s.status?.failed && <li>none</li>}
          </ul>
        </div>
      </div>
      {s.daily.length > 1 && (
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">Per day: customer tickets, and the part Replai answered</div>
          <div className="mt-2 flex h-28 items-end gap-1">
            {s.daily.map((x) => (
              <div key={x.date} className="flex h-full flex-1 flex-col justify-end" title={`${x.date}: ${x.sent} of ${x.tickets} answered by Replai (${share(x.sent, x.tickets)})`}>
                <div className="relative w-full rounded-t bg-[var(--line-2)]" style={{ height: `${(x.tickets / max) * 100}%` }}>
                  <div className="absolute inset-x-0 bottom-0 rounded-t" style={{ height: `${x.tickets ? (x.sent / x.tickets) * 100 : 0}%`, background: BLUE }} />
                </div>
              </div>))}
          </div>
          <div className="mt-1 flex justify-between text-xs text-[var(--ink-3)]"><span>{s.daily[0].date.slice(5)}</span><span>{s.daily[s.daily.length - 1].date.slice(5)}</span></div>
        </div>
      )}
      {s.categories.length > 0 && (
        <table className="mt-6 w-full text-sm">
          <thead className="text-left text-xs text-[var(--ink-3)]"><tr><th className="py-1 font-medium">Topic</th><th className="py-1 text-right font-medium">Tickets</th><th className="py-1 text-right font-medium">Queued</th><th className="py-1 text-right font-medium">Sent by Replai</th><th className="py-1 text-right font-medium">Share</th></tr></thead>
          <tbody>{s.categories.map((c) => (
            <tr key={c.category} className="border-t border-[var(--line-2)]">
              <td className="py-1.5">{CAT_LABEL[c.category] || c.category}</td>
              <td className="num py-1.5 text-right">{c.tickets}</td><td className="num py-1.5 text-right">{c.queued}</td>
              <td className="num py-1.5 text-right">{c.sent || 0}</td><td className="num py-1.5 text-right font-semibold">{share(c.sent || 0, c.tickets)}</td>
            </tr>))}</tbody>
        </table>
      )}
    </section>
  );
};

const DashboardV2 = () => {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [asTable, setAsTable] = useState(false);
  useEffect(() => {
    setD(null);
    axiosInstance.get('api/replai/v2/dashboard/', { params: { days } }).then(({ data }) => setD(data)).catch(() => setD(false));
  }, [days]);
  const k = d && d.kpi;

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Dashboard</h1>
            <p className="prose-body mt-2 text-[var(--ink-2)]">
              Notes from the Bible agent only{d && d.since ? `, live since ${new Date(d.since).toLocaleString(undefined, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}` : ''}.
              Outcomes update every hour.
            </p>
          </div>
          <div className="flex gap-1">
            {[1, 7, 30].map((n) => (
              <button key={n} type="button" onClick={() => setDays(n)}
                className={`rounded-md px-3 py-1.5 text-sm ${days === n ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
                {n === 1 ? 'Today' : `${n} days`}
              </button>
            ))}
          </div>
        </div>

        {d === false && <p className="mt-8 text-[var(--ink-2)]">The dashboard could not be loaded. Reload the page to try again.</p>}
        {d == null && <div className="mt-8 h-80 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {k && (
          <>
            <section className="mt-8 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
              <h2 className="text-[1.2rem] font-semibold">From ticket to sent reply</h2>
              <p className="mt-1 text-[var(--ink-2)]">How many customer tickets made it to a reply the team sent, and where the others went.</p>
              <div className="mt-5">
                <Funnel steps={[
                  { label: 'Customer tickets', value: k.tickets },
                  { label: 'Note posted', value: k.notes, drop: [{ value: k.held, label: 'no note posted' }] },
                  { label: 'Reply drafted', value: k.notes - k.handoffs, drop: [{ value: k.handoffs, label: 'notes for an agent' }] },
                  { label: 'Team has answered', value: k.replied, drop: [{ value: k.waiting, label: 'still waiting for the team' }] },
                  { label: 'Draft used', value: k.used, drop: [{ value: k.replied - k.used, label: 'replaced by the team' }] },
                  { label: 'Sent as written', value: k.verbatim, drop: [{ value: k.used - k.verbatim, label: 'sent after edits' }] },
                ]} />
              </div>
              <p className="mt-4 text-sm text-[var(--ink-3)]">Not counted: {k.noise} carrier, supplier and automatic mails, which get no note on purpose.</p>
            </section>

            {d.scheduled && <ScheduledStats s={d.scheduled} />}

            <section className="mt-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
              <h2 className="text-[1.2rem] font-semibold">Is Replai getting better?</h2>
              <p className="mt-1 mb-4 text-sm text-[var(--ink-3)]">Share of Replai's notes the team rated 👎, as a 7-day moving average.</p>
              <FeedbackTrend days={60} />
            </section>

            <section className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <RingKpi value={pct(k.used, k.replied)} label="Drafts the team used"
                detail={k.replied ? `${k.used} of ${k.replied} answered notes, ${k.verbatim} sent as written` : `${k.waiting} waiting for a reply`} />
              <RingKpi value={pct(k.right, k.rated)} label="Rated right by the team" color="#1baf7a"
                detail={k.rated ? `${k.right} right, ${k.rated - k.right} wrong` : 'no ratings yet: use 👍 / 👎 on a note'} />
              <RingKpi value={k.replied ? 100 - pct(k.wrote_again, k.replied) : null} label="No follow-up needed" color="#4a3aa7"
                detail={k.replied ? `${k.wrote_again} customers wrote again after the team's reply` : 'appears once the team has answered'} />
              <TimelineKpi toNote={k.minutes_to_note} toReply={k.minutes_to_reply} />
            </section>
            <section className="mt-4 grid grid-cols-2 gap-4 xl:grid-cols-4">
              <CountKpi value={k.notes} label="Notes posted" detail={`${k.tickets} customer tickets`}
                values={d.daily.map((r) => OUTCOMES.reduce((t, o) => t + r[o.key], 0))} dates={d.daily.map((r) => r.date)} />
              <CountKpi value={k.handoffs} label="Notes for an agent" detail="a person had to act or decide"
                values={d.daily.map((r) => r.handoff)} dates={d.daily.map((r) => r.date)} />
              <CountKpi value={k.check_first} label="Check before sending" detail={`${k.guards} caught by a safety rule`} />
              <CountKpi value={k.held} label="No note posted" detail="held back or failed"
                values={d.daily.map((r) => r.held)} dates={d.daily.map((r) => r.date)} />
            </section>

            <section className="mt-10 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h2 className="text-[1.2rem] font-semibold">What the team did with each note</h2>
                <button type="button" onClick={() => setAsTable(!asTable)} className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)]">{asTable ? 'Show chart' : 'Show as table'}</button>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--ink-2)]">
                {OUTCOMES.map((o) => (
                  <span key={o.key} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: o.color }} />{o.label}</span>
                ))}
              </div>
              {asTable ? (
                <div className="mt-4"><Table head={['Day', ...OUTCOMES.map((o) => o.label), 'No note']}>
                  {[...d.daily].reverse().map((r) => (
                    <tr key={r.date} className="border-b border-[var(--line-2)]"><td className="py-1.5">{day(r.date)}</td>{OUTCOMES.map((o) => <td key={o.key}>{r[o.key]}</td>)}<td>{r.held}</td></tr>
                  ))}
                </Table></div>
              ) : <div className="mt-4"><DailyChart rows={d.daily} /></div>}
            </section>

            <section className="mt-10 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-6">
              <h2 className="text-[1.2rem] font-semibold">Problems</h2>
              <p className="mt-1 text-[var(--ink-2)]">Tickets where something went wrong on our side. Failed tickets are retried once after an hour.</p>
              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
                {[
                  ['failed', 'No draft at all', 'both versions failed'],
                  ['fallback', 'Old Replai took over', 'the Bible agent gave no answer'],
                  ['post_failed', 'Note not posted', 'Gorgias refused the note'],
                  ['recovered', 'Recovered hiccups', 'a Ferry step failed, the next worked'],
                  ['quality_retry', 'Rewritten after a check', 'too short, no Bible or no product check: sent back once'],
                ].map(([key, l, hint]) => (
                  <div key={key}>
                    <div className={`num text-[1.8rem] font-semibold leading-none ${(d.problems.counts[key] || 0) && !['recovered', 'quality_retry'].includes(key) ? 'text-red-700' : ''}`}>{d.problems.counts[key] || 0}</div>
                    <div className="mt-1.5 font-medium">{l}</div>
                    <div className="text-sm text-[var(--ink-3)]">{hint}</div>
                  </div>
                ))}
              </div>
              {d.problems.recent.length > 0 && (
                <ul className="mt-5 divide-y divide-[var(--line-2)] border-t border-[var(--line-2)]">
                  {d.problems.recent.map((p) => (
                    <li key={p.run} className="py-2.5">
                      <Link to={`/tickets/${p.run}`} className="font-medium hover:underline">{p.subject || 'No subject'}</Link>
                      <span className="block text-sm text-[var(--ink-2)]">
                        {{ failed: 'No draft', fallback: 'Old Replai took over', post_failed: 'Note not posted', recovered: 'Recovered' }[p.kind]}
                        {p.retried ? ' (retried)' : ''}, {timeAgo(p.at)}: <span className="text-[var(--ink-3)]">{p.reason}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-10 grid gap-10 xl:grid-cols-2">
              <div>
                <h2 className="text-[1.2rem] font-semibold">Review queue</h2>
                <p className="mt-1 text-[var(--ink-2)]">Notes the team replaced or rated wrong. Each one points to a Bible rule or a data gap to fix.</p>
                {!d.review.length ? <p className="mt-4 text-[var(--ink-2)]">Nothing to review yet.</p> : (
                  <ul className="mt-3 divide-y divide-[var(--line-2)]">
                    {d.review.map((n) => (
                      <li key={n.run} className="py-2.5">
                        <Link to={`/tickets/${n.run}`} className="font-medium hover:underline">{n.subject || 'No subject'}</Link>
                        <span className="block text-sm text-[var(--ink-2)]">
                          {n.rating === 'incorrect' ? 'Rated wrong' : 'Replaced'}{n.agent ? ` by ${n.agent}` : ''}, {label(n.category)}, {timeAgo(n.at)}
                          {n.note ? `: "${n.note}"` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h2 className="text-[1.2rem] font-semibold">By team member</h2>
                <p className="mt-1 text-[var(--ink-2)]">Who answered after a note, and how often they used it.</p>
                <div className="mt-3"><Table head={['Agent', 'Replies', 'Used the draft', 'Reply time']}>
                  {d.team.map((t) => (
                    <tr key={t.agent} className="border-b border-[var(--line-2)]"><td className="py-2">{t.agent}</td><td>{t.replies}</td><td><span className="inline-flex items-center gap-2"><span className="inline-block h-2 w-16 overflow-hidden rounded-full bg-[var(--line-2)]"><span className="block h-full rounded-full" style={{ width: `${pct(t.used, t.replies)}%`, background: BLUE }} /></span>{pct(t.used, t.replies)}%</span></td><td>{mins(t.minutes) ?? '-'}</td></tr>
                  ))}
                  {!d.team.length && <tr><td colSpan={4} className="py-3 text-[var(--ink-3)]">No team replies after a note yet.</td></tr>}
                </Table></div>
              </div>
            </section>

            <section className="mt-10 grid gap-10 xl:grid-cols-2">
              <div>
                <h2 className="text-[1.2rem] font-semibold">By topic</h2>
                <p className="mt-1 text-[var(--ink-2)]">What the team did with the notes on each topic.</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--ink-2)]">
                  {OUTCOMES.map((o) => (
                    <span key={o.key} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: o.color }} />{o.label}</span>
                  ))}
                </div>
                <ul className="mt-4 space-y-3">
                  {d.categories.map((c) => (
                    <li key={c.category}>
                      <div className="flex items-baseline justify-between gap-3 text-[0.95rem]">
                        <span className="font-medium first-letter:uppercase">{label(c.category)}</span>
                        <span className="num text-sm text-[var(--ink-2)]">
                          {c.notes} notes{c.replied ? `, ${pct(c.used, c.replied)}% used` : ''}{c.replied && c.again ? `, ${pct(c.again, c.replied)}% wrote again` : ''}
                        </span>
                      </div>
                      <div className="mt-1.5"><OutcomeBar row={c} /></div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-[1.2rem] font-semibold">Bible rules on answered notes</h2>
                <p className="mt-1 text-[var(--ink-2)]">A rule whose drafts keep getting replaced is the first one to review.</p>
                <div className="mt-3"><Table head={['Rule', 'Answered', 'Used']}>
                  {d.bible.map((b) => (
                    <tr key={b.id} className="border-b border-[var(--line-2)]">
                      <td className="py-2"><Link to={b.path} className="font-semibold text-[var(--brass)] hover:underline">{b.label}</Link></td>
                      <td>{b.replied}</td><td>{pct(b.used, b.replied)}%</td>
                    </tr>
                  ))}
                  {!d.bible.length && <tr><td colSpan={3} className="py-3 text-[var(--ink-3)]">Appears once the team has answered notes.</td></tr>}
                </Table></div>
              </div>
            </section>
          </>
        )}
      </div>
    </V2Shell>
  );
};

export default DashboardV2;
